import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import logo from 'assets/img/auth/usibras_ghana_limited_logo.jpeg';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const months = [
  { key: 'jan', label: 'JAN. 2025' },
  { key: 'feb', label: 'FEB. 2025' },
  { key: 'mar', label: 'MAR. 2025' },
  { key: 'apr', label: 'APR. 2025' },
  { key: 'may', label: 'MAY. 2025' },
  { key: 'jun', label: 'JUN. 2025' },
  { key: 'jul', label: 'JUL. 2025' },
  { key: 'aug', label: 'AUG. 2025' },
  { key: 'sep', label: 'SEP. 2025' },
  { key: 'oct', label: 'OCT. 2025' },
  { key: 'nov', label: 'NOV. 2025' },
  { key: 'dec', label: 'DEC. 2025' },
];

const sampleData = [
  {
    product: 'WW 450',
    inventory: 101,
    jan: { sale: 0, aval: 101 },
    feb: { sale: 0, aval: 101 },
    mar: { sale: 0, aval: 101 },
    apr: { sale: 0, aval: 101 },
    may: { sale: 0, aval: 101 },
    jun: { sale: 0, aval: 101 },
    jul: { sale: 0, aval: 101 },
    aug: { sale: 0, aval: 101 },
    sep: { sale: 0, aval: 101 },
    oct: { sale: 0, aval: 101 },
    nov: { sale: 0, aval: 101 },
    dec: { sale: 0, aval: 101 },
  },
  {
    product: 'WW 320',
    inventory: 825,
    jan: { sale: 0, aval: 825 },
    feb: { sale: 0, aval: 825 },
    mar: { sale: 0, aval: 825 },
    apr: { sale: 0, aval: 825 },
    may: { sale: 0, aval: 825 },
    jun: { sale: 700, aval: 125 },
    jul: { sale: 0, aval: 125 },
    aug: { sale: 0, aval: 125 },
    sep: { sale: 0, aval: 125 },
    oct: { sale: 0, aval: 125 },
    nov: { sale: 0, aval: 125 },
    dec: { sale: 0, aval: 125 },
  },
  {
    product: 'SK1',
    inventory: 30,
    jan: { sale: 0, aval: 30 },
    feb: { sale: 0, aval: 30 },
    mar: { sale: 0, aval: 30 },
    apr: { sale: 0, aval: 30 },
    may: { sale: 0, aval: 30 },
    jun: { sale: 0, aval: 30 },
    jul: { sale: 0, aval: 30 },
    aug: { sale: 0, aval: 30 },
    sep: { sale: 0, aval: 30 },
    oct: { sale: 0, aval: 30 },
    nov: { sale: 0, aval: 30 },
    dec: { sale: 0, aval: 30 },
  },
  // ... add more sample rows as needed
];

const totals = {
  inventory: 956,
  jan: { sale: 0, aval: 956 },
  feb: { sale: 0, aval: 956 },
  mar: { sale: 0, aval: 956 },
  apr: { sale: 0, aval: 956 },
  may: { sale: 0, aval: 956 },
  jun: { sale: 700, aval: 256 },
  jul: { sale: 0, aval: 256 },
  aug: { sale: 0, aval: 256 },
  sep: { sale: 0, aval: 256 },
  oct: { sale: 0, aval: 256 },
  nov: { sale: 0, aval: 256 },
  dec: { sale: 0, aval: 256 },
};

const visibleMonths = months.slice(0, 4); // Show only 4 months at a time
const hiddenMonths = months.slice(4); // The rest are scrollable

const SalesForecastReport = () => {
  const navigate = useNavigate();
  const currentDate = new Date();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [filteredDate, setFilteredDate] = useState(currentDate);
  const formattedDate = filteredDate.toLocaleDateString('en-CA');

  const handleFilter = () => {
    setFilteredDate(selectedDate);
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 10;

    // Logo
    const img = new window.Image();
    img.src = logo;
    doc.addImage(img, 'JPEG', 10, y, 30, 30);

    // Title and Date
    doc.setFontSize(18);
    doc.text('Report Sales Forecast', pageWidth / 2, y + 10, { align: 'center' });
    doc.setFontSize(12);
    doc.text(`Date: ${formattedDate}`, pageWidth / 2, y + 18, { align: 'center' });

    // Table headers
    const head = [
      ['Product', 'Inventory', ...months.flatMap(m => [m.label + ' Sale', m.label + ' Aval'])]
    ];
    // Table body
    const body = sampleData.map(row => [
      row.product,
      row.inventory,
      ...months.flatMap(m => [row[m.key].sale, row[m.key].aval])
    ]);
    // Table foot
    const foot = [
      [
        'Total:',
        totals.inventory,
        ...months.flatMap(m => [totals[m.key].sale, totals[m.key].aval])
      ]
    ];

    autoTable(doc, {
      startY: y + 32,
      head,
      body,
      foot,
      headStyles: { fillColor: [22, 160, 133] },
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
      theme: 'grid',
      styles: { fontSize: 8 },
      margin: { left: 10, right: 10 },
    });
    doc.save('sales-forecast-report.pdf');
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors duration-200 self-start"
          >
            <span className="font-medium">Back to Reports</span>
          </button>
          {/* Date Filter and Download */}
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <span className="font-medium text-gray-700">Select Date:</span>
            <DatePicker
              selected={selectedDate}
              onChange={date => setSelectedDate(date)}
              dateFormat="yyyy-MM-dd"
              className="pl-2 pr-2 py-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleFilter}
              className="ml-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Filter
            </button>
            <button
              onClick={downloadPdf}
              className="ml-2 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              Download PDF
            </button>
          </div>
        </div>

        {/* Logo, Title, and Date */}
        <div className="flex flex-col items-center justify-center mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <img src={logo} alt="Usibras Ghana Limited Logo" className="h-20 w-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-center">Report Sales Forecast</h1>
          <p className="text-base md:text-lg text-gray-600 text-center">{formattedDate}</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="min-w-full text-xs md:text-sm" style={{ minWidth: '900px' }}>
            <thead>
              <tr className="bg-gray-100">
                <th className="px-2 py-2 border text-left font-bold align-middle" rowSpan={2}>Product</th>
                <th className="px-2 py-2 border text-right font-bold align-middle" rowSpan={2}>Inventory</th>
                {/* Month headers with colSpan=2 */}
                {months.map((month) => (
                  <th key={month.key + '-header'} className="px-2 py-2 border text-center font-bold" colSpan={2}>{month.label}</th>
                ))}
              </tr>
              <tr className="bg-gray-50">
                {/* Sub-headers for Sale and Aval under each month */}
                {months.map((month) => [
                  <th key={month.key + '-sale'} className="px-2 py-2 border text-center font-medium">Sale</th>,
                  <th key={month.key + '-aval'} className="px-2 py-2 border text-center font-medium">Aval</th>
                ])}
              </tr>
            </thead>
            <tbody>
              {sampleData.map((row, idx) => (
                <tr key={row.product} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-2 py-1 border font-medium text-gray-900 whitespace-nowrap">{row.product}</td>
                  <td className="px-2 py-1 border text-right">{row.inventory}</td>
                  {months.map((month) => [
                    <td key={month.key + '-sale'} className="px-2 py-1 border text-right">{row[month.key].sale}</td>,
                    <td key={month.key + '-aval'} className="px-2 py-1 border text-right">{row[month.key].aval}</td>
                  ])}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-2 border">Total:</td>
                <td className="px-2 py-2 border text-right">{totals.inventory}</td>
                {months.map((month) => [
                  <td key={month.key + '-sale-tot'} className="px-2 py-2 border text-right">{totals[month.key].sale}</td>,
                  <td key={month.key + '-aval-tot'} className="px-2 py-2 border text-right">{totals[month.key].aval}</td>
                ])}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesForecastReport; 